/**
 * Properties of a Machine
 */

import { array, boolean, object, string, type InferType } from "yup"
import { MachineTermsEntrySchema } from "../types/EuDataAct"

export const MachinesOrganizationsSchema = object({
  org_id: string().uuid().required(),
  machine_id: string().uuid().optional(),
})
export type MachinesOrganizationsDto = InferType<
  typeof MachinesOrganizationsSchema
>

/** Machine as in supabase */
export const MachineSupaSchema = object({
  created_at: string().required(),
  created_by: string().required(),
  deleted_at: string().nullable(),
  deleted_by: string().nullable(),
  id: string().required(),
  machine_attributes: string().nullable(), // JSONB
  machine_type: string().nullable(),
  modem_serial: string().required(),
  name: string().required(),
  serial_number: string().required(),
  updated_at: string().defined(),
  updated_by: string().defined(),

  // Derived properties
  EuDataActTerms: array(MachineTermsEntrySchema),
  hasActiveDnv: boolean(),
  MachinesOrganizations: array(MachinesOrganizationsSchema).nullable(), // Is set in getById, otherwise it's null
})

/**
 * Create-DTO: All data that's transferred from FE to BE for a create request.
 */
export const MachineSupaCreateSchema = MachineSupaSchema.omit([
  "created_at", // filled by backend
  "created_by", // filled by backend
  "updated_at", // filled by backend
  "updated_by", // filled by backend
  "deleted_at", // makes no sense for create
  "deleted_by", // makes no sense for create
  "id", // not for create
  "EuDataActTerms", // joined from another table
  "hasActiveDnv", // derived from EuDataActTerms
  "MachinesOrganizations", // joined from another table
])

/**
 * Update-DTO: All data that's transferred from FE to BE for a update request.
 *
 * @note Of course, `id` is required for an update. It is taken from the path.
 */
export const MachineSupaUpdateSchema = MachineSupaSchema.omit([
  "created_at", // cannot be updated
  "created_by", // cannot be updated
  "updated_at", // filled by backend
  "updated_by", // filled by backend
  "id", // cannot be updated
  "EuDataActTerms", // joined from another table
  "hasActiveDnv", // derived from EuDataActTerms
  "MachinesOrganizations", // joined from another table
])

export type MachineSupaDto = InferType<typeof MachineSupaSchema>
export type MachineSupaCreateDto = InferType<typeof MachineSupaCreateSchema>
export type MachineSupaUpdateDto = InferType<typeof MachineSupaUpdateSchema>

export const MachineDdbSchema = object({
  PK: string().required(), // === ModemSerial
  SK: string().required(), // alway "DeviceManagement"?
  device_type: string(),
  einrichtungAm: string(),
  ext_sn: string(),
  firmwareVersion: string(), // serialized JSON
  letztesUpdateAm: string(),
  m_fahrgestellnummer: string(),
  m_machineType: string().optional(),
  machine_status: string(),
  os_release: string(), // serialized JSON
  rules: string(), // serialized JSON
  seriennummer: string(), // === PK?
})

export type MachineDdbDto = InferType<typeof MachineDdbSchema>
// export type MachineDdbUpdateDto = InferType<typeof MachineDdbUpdateSchema>
